import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { ConfirmTransactionTemplate } from '@/components'
import { useWithdrawStore } from '@/store/useWithdrawStore'
import { useUserStore } from '@/store/useUserStore'
import { SelectBlockchainModal } from '@/features/currency-deposit/components'
import { useGetBlockchainsCatalogs } from '@/domain/blockchains/useCases/useGetBlockchainsCatalogs'
import type { BlockchainCatalogsDTO } from '@/domain/blockchains/blockchainsApi'
import { currencyUtils, mapAxiosErrorToApiError } from '@/utils'
import {
  useCalculateExternalWithdrawFee,
  useCreateExternalWithdraw,
} from '@/domain/transfer/useCases'
import {
  useFetchWithdrawCryptoAddressByNetworkCode,
  useCreateWithdrawCryptoAddress,
} from '@/domain/wallet'

const { formatFiatCurrency } = currencyUtils

export const WithdrawReviewAndConfirmScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const wallet = useUserStore((state) => state.wallet)
  const externalWalletAddress = useWithdrawStore(
    (state) => state.externalWalletAddress,
  )
  const blockchain = useWithdrawStore((state) => state.blockchain)
  const cryptoAmount = useWithdrawStore((state) => state.cryptoAmount)
  const fiatAmount = useWithdrawStore((state) => state.fiatAmount)
  const selectedCryptoWalletData = useWithdrawStore(
    (state) => state.selectedCryptoWalletData,
  )
  const setExternalWalletAddress = useWithdrawStore(
    (state) => state.setExternalWalletAddress,
  )
  // const resetExternalWalletAddress = useWithdrawStore(
  //   (state) => state.resetExternalWalletAddress,
  // )
  const setBlockchain = useWithdrawStore((state) => state.setBlockchain)
  const mfaPayload = useWithdrawStore((state) => state.mfaPayload)
  const resetMfaPayload = useWithdrawStore((state) => state.resetMfaPayload)

  // Store state for blockchains modal
  const isBlockchainModalOpen = useWithdrawStore(
    (state) => state.isBlockchainModalOpen,
  )
  const setIsBlockchainModalOpen = useWithdrawStore(
    (state) => state.setIsBlockchainModalOpen,
  )

  const hasExternalWalletAddress = !!externalWalletAddress?.address
  const blockchainNetworkCode = blockchain?.code
  const walletId = wallet?.walletId
  const currency = wallet?.completeCurrencies?.find(
    (currency) => currency.code === cryptoAmount.code,
  )

  const formattedFiatAmount = formatFiatCurrency({
    value: Number(fiatAmount.amount || 0),
    fiatCode: fiatAmount.code,
    showDecimals: true,
  })

  // Get blockchain list for selected currency
  const { data: blockchains } = useGetBlockchainsCatalogs({
    currency_isin: (cryptoAmount?.code as any) || 'USDT',
  })

  // Safe access to blockchains array
  const blockchainsList =
    (blockchains as { blockchains: BlockchainCatalogsDTO[] } | undefined)
      ?.blockchains || []

  // Get saved addresses for selected network
  const { data: savedAddresses, isLoading: isLoadingAddresses } =
    useFetchWithdrawCryptoAddressByNetworkCode({
      networkCode: blockchainNetworkCode || '',
    })

  // Fee calculation (as in RN version)
  const {
    data: fee,
    isLoading: isCalculatingFee,
    isRefetching: isCalculatingFeeRefetching,
    error: calculateFeeError,
  } = useCalculateExternalWithdrawFee({
    amount: Number(cryptoAmount.amount || 0),
    currency: cryptoAmount.code as any,
    blockchain_network: blockchainNetworkCode || '',
    destination_address: externalWalletAddress?.address || '',
  })

  const isLoadingFee = isCalculatingFee || isCalculatingFeeRefetching

  const {
    mutateAsync: createWithdrawCryptoAddress,
    isPending: isLoadingCreateWithdrawCryptoAddress,
  } = useCreateWithdrawCryptoAddress({
    onSuccess: (payload) => {
      toast.success(t('common:addressRegistered'))

      resetMfaPayload()
      setExternalWalletAddress({
        address: payload.address,
        name: payload.name,
      })
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage ?? t('api:errors.somethingWrong'))
    },
  })

  const {
    mutateAsync: createExternalWithdraw,
    isPending: isLoadingExternalWithdraw,
  } = useCreateExternalWithdraw()

  const handleCloseBlockchainModal = () => {
    setIsBlockchainModalOpen(false)
  }

  const handleBlockchainSelect = (
    selectedBlockchain: BlockchainCatalogsDTO,
  ) => {
    // Reset address only if a different blockchain is selected
    if (blockchain?.code !== selectedBlockchain.code) {
      setExternalWalletAddress({
        name: '',
        address: '',
      })
    }

    setBlockchain(selectedBlockchain)
    handleCloseBlockchainModal()
  }

  const handleBackPress = () => {
    // Get source page from state if available
    const fromPage = (location.state as any)?.from

    navigate('/withdraw', {
      state: {
        ...(fromPage ? { from: fromPage } : {}),
        ...(selectedCryptoWalletData
          ? {
              currency: selectedCryptoWalletData,
              selectedCurrency: selectedCryptoWalletData,
              cryptoCode: selectedCryptoWalletData.code,
            }
          : {}),
      },
    })
  }

  function validateWithdraw() {
    if (!fee) {
      toast.error(t('api:errors.somethingWrong'))
      return false
    }

    if (
      Number(fee.total_amount.amount) <= Number(fee.fee_crypto_amount.amount)
    ) {
      toast.error(t('api:validationErrors.insufficientBalance'))
      return false
    }

    return true
  }

  function handleNextWithdraw() {
    if (!hasExternalWalletAddress) {
      navigate('/withdraw/choose-address')
      return
    }

    if (!validateWithdraw()) return

    handleConfirmWithdraw()
  }

  function onWithdrawError(error: unknown) {
    const { code } = mapAxiosErrorToApiError(error)

    let errorMessage = t('api:errors.somethingWrong')

    if (code === 'INVALID_CRYPTO_ADDRESS_ERROR') {
      errorMessage = t('api:pleaseUseAnotherAddress')
    }

    if (code === ('INSUFFICIENT_FUNDS_ERROR' as any)) {
      errorMessage = t('api:validationErrors.insufficientBalance')
    }

    if (code === ('WITHDRAW_DISABLED' as any)) {
      // Can add navigation to warning screen
      errorMessage = t('screens:pausedWithdrawals.description')
    }

    toast.error(errorMessage)
  }

  function navigateToSuccessScreen() {
    const paramsData = {
      amount: cryptoAmount?.amount,
      code: cryptoAmount?.code || 'USDT',
      title: t('screens:withdraw.youWithdrew'),
      relativeFiatFormatted: formattedFiatAmount,
    }

    navigate('/withdraw/success', {
      state: paramsData,
    })
  }

  async function handleConfirmWithdraw() {
    try {
      await createExternalWithdraw({
        amount: Number(fee?.total_amount.amount || 0),
        currency: fee?.total_amount.currency || 'USDT',
        blockchain_network: blockchainNetworkCode || '',
        destination_address: externalWalletAddress?.address || '',
        wallet_id: walletId || '',
        fee_amount: fee?.fee_crypto_amount?.amount
          ? Number(fee.fee_crypto_amount.amount)
          : 0,
      })

      navigateToSuccessScreen()
    } catch (error) {
      onWithdrawError(error)
    }
  }

  useEffect(() => {
    if (calculateFeeError) {
      const { code } = mapAxiosErrorToApiError(calculateFeeError)

      if (code === ('WITHDRAW_DISABLED' as any)) {
        // Can add navigation to warning screen
        toast.error(t('screens:pausedWithdrawals.description'))
        return
      }

      toast.error(t('api:errors.somethingWrong'))
    }
  }, [calculateFeeError, t])

  // Handle return from MFA screen with token
  useEffect(() => {
    const verifyMfaToken = async () => {
      const locationState = location.state as { token?: string } | undefined
      const mfaToken = locationState?.token

      if (
        mfaToken &&
        mfaPayload &&
        mfaPayload.address &&
        mfaPayload.name &&
        mfaPayload.network_code
      ) {
        await createWithdrawCryptoAddress({
          grantToken: mfaToken,
          network_code: mfaPayload.network_code,
          address: mfaPayload.address,
          name: mfaPayload.name,
        })
      }
    }

    verifyMfaToken()
  }, [location.state, mfaPayload, createWithdrawCryptoAddress])

  // Removed useEffect that reset address on unmount
  // useEffect(() => {
  //   // Clear wallet address on component unmount
  //   return () => resetExternalWalletAddress()
  // }, [resetExternalWalletAddress])

  return (
    <>
      <ConfirmTransactionTemplate
        byScreen="withdraw"
        screenTitle={t('screens:withdraw.title')}
        buttonTitle={t('form:actions.next')}
        onSubmit={handleNextWithdraw}
        onBackPress={handleBackPress}
        currency={currency}
        transactionFee={{
          amount: String(fee?.fee_crypto_amount?.amount || 0),
          currency: fee?.fee_crypto_amount?.currency || 'USDT',
        }}
        total={{
          amount: String(fee?.total_amount?.amount || 0),
          currency: fee?.total_amount?.currency || 'USDT',
        }}
        amountInCrypto={cryptoAmount.amount}
        amount={{
          amount: fiatAmount.amount,
          currency: fiatAmount.code,
        }}
        selectedBlockchain={blockchain}
        externalWalletAddress={externalWalletAddress}
        savedAddressesCount={savedAddresses?.crypto_addresses?.length || 0}
        isLoadingAddresses={isLoadingAddresses}
        onSelectBlockchain={() => {
          setIsBlockchainModalOpen(true)
        }}
        buttonDisabled={!hasExternalWalletAddress}
        isLoadingSubmit={
          isLoadingExternalWithdraw ||
          isLoadingFee ||
          isLoadingCreateWithdrawCryptoAddress
        }
        transactionFeeIsLoading={isLoadingFee}
        infoCurrency={`≈${formattedFiatAmount}`}
      />

      {/* Blockchain Selection Modal */}
      <SelectBlockchainModal
        isOpen={isBlockchainModalOpen}
        onClose={handleCloseBlockchainModal}
        onSelect={handleBlockchainSelect}
        blockchains={blockchainsList}
        selectedBlockchain={blockchain}
        currencyCode={cryptoAmount?.code || ''}
      />
    </>
  )
}
